import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Plus, Filter, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { QuickTimelogButton } from '@/components/pm/timelog/quick-timelog-button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useKanbanData,
  useUpdateIssueStatus,
  type IssueStatus,
  type IssuePriority,
  type IssueCard,
  type KanbanFilters,
  useProject, type DeveloperGroup
} from '@/hooks'
import { cn } from '@/lib/utils'

/**
 * 看板页面
 */
export const Route = createFileRoute('/_authenticated/pm/projects/$projectId/board')({
  component: KanbanBoardPage,
})

const STATUS_LABELS: Record<IssueStatus, string> = {
  backlog: '待办',
  todo: '计划中',
  in_progress: '进行中',
  code_review: '代码审查',
  testing: '测试中',
  done: '已完成',
  blocked: '已阻塞',
  cancelled: '已取消',
}

const STATUS_COLORS: Record<IssueStatus, string> = {
  backlog: 'border-t-gray-400',
  todo: 'border-t-blue-400',
  in_progress: 'border-t-yellow-400',
  code_review: 'border-t-purple-400',
  testing: 'border-t-orange-400',
  done: 'border-t-green-400',
  blocked: 'border-t-red-400',
  cancelled: 'border-t-gray-300',
}

const PRIORITY_COLORS: Record<IssuePriority, string> = {
  urgent: 'border-l-red-500',
  high: 'border-l-orange-500',
  medium: 'border-l-yellow-500',
  low: 'border-l-gray-400',
}

const KANBAN_STATUSES: IssueStatus[] = [
  'backlog',
  'todo',
  'in_progress',
  'code_review',
  'testing',
  'done',
]

const DEVELOPER_GROUP_LABELS: Record<DeveloperGroup, string> = {
  product: '产品',
  design: '设计',
  frontend: '前端',
  backend: '后端',
  testing: '测试',
}

function KanbanBoardPage() {
  const params = Route.useParams()
  const projectId = params.projectId
  const navigate = useNavigate()
  const [filters, setFilters] = useState<KanbanFilters>({})
  const [showFilters, setShowFilters] = useState(false)
  const [draggedCard, setDraggedCard] = useState<IssueCard | null>(null)

  const { data: project } = useProject(projectId)
  const { data: kanbanData, isLoading, error } = useKanbanData(projectId, filters)
  const updateStatusMutation = useUpdateIssueStatus()

  const handleDeveloperGroupFilter = (group: DeveloperGroup | undefined) => {
    setFilters((prev) => ({ ...prev, developerGroup: group }))
  }

  const handleDragStart = (card: IssueCard) => {
    setDraggedCard(card)
  }

  const handleDragEnd = () => {
    setDraggedCard(null)
  }

  const handleDrop = (targetStatus: IssueStatus) => {
    if (!draggedCard) {
      return
    }

    // Find current status of the card
    const currentColumn = kanbanData?.columns.find((col) =>
      col.issues.some((issue) => issue.id === draggedCard.id)
    )

    if (currentColumn && currentColumn.status !== targetStatus) {
      updateStatusMutation.mutate({
        id: draggedCard.id,
        data: { status: targetStatus },
      })
    }

    setDraggedCard(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">加载中...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-destructive">加载失败</div>
        <Link
          to="/pm/projects/$projectId"
          params={{ projectId }}
          className="text-sm text-primary hover:underline"
        >
          返回项目详情
        </Link>
      </div>
    )
  }

  // Build columns map for easy access
  const columnsMap = new Map<IssueStatus, IssueCard[]>(
    kanbanData?.columns.map((col) => [col.status, col.issues] as [IssueStatus, IssueCard[]]) || []
  )

  return (
    <div className="flex h-full flex-col">
      {/* 头部 */}
      <div className="flex items-center gap-4 pb-4">
        <button
          onClick={() => void navigate({ to: '/pm/projects/$projectId', params: { projectId } })}
          className="rounded-md p-2 hover:bg-muted"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <p className="text-sm text-muted-foreground">{project?.code}</p>
          <h1 className="text-2xl font-bold tracking-tight">看板</h1>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={cn(
            'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
            showFilters ? 'bg-muted' : 'hover:bg-muted'
          )}
        >
          <Filter className="h-4 w-4" />
          筛选
          <ChevronDown className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')} />
        </button>
        <button
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          新建任务
        </button>
      </div>

      {/* 筛选面板 */}
      {showFilters && (
        <div className="mb-4 rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">开发组</label>
              <Select
                value={filters.developerGroup ?? 'all'}
                onValueChange={(value) => handleDeveloperGroupFilter(value === 'all' ? undefined : value as DeveloperGroup)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="全部" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  {(Object.keys(DEVELOPER_GROUP_LABELS) as DeveloperGroup[]).map((group) => (
                    <SelectItem key={group} value={group}>
                      {DEVELOPER_GROUP_LABELS[group]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {filters.developerGroup && (
              <button
                onClick={() => setFilters({})}
                className="mt-5 text-sm text-muted-foreground hover:text-foreground"
              >
                重置筛选
              </button>
            )}
          </div>
        </div>
      )}

      {/* 看板 */}
      <div className="flex flex-1 gap-4 overflow-x-auto pb-4">
        {KANBAN_STATUSES.map((status) => {
          const issues = columnsMap.get(status) || []

          return (
            <div
              key={status}
              className={cn(
                'flex w-72 shrink-0 flex-col rounded-lg border border-t-4 bg-muted/30',
                STATUS_COLORS[status]
              )}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(status)}
            >
              {/* 列头 */}
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{STATUS_LABELS[status]}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                    {issues.length}
                  </span>
                </div>
              </div>

              {/* 卡片列表 */}
              <div className="flex-1 space-y-2 overflow-y-auto p-2">
                {issues.map((card) => (
                  <KanbanCard
                    key={card.id}
                    card={card}
                    projectId={projectId}
                    onDragStart={() => handleDragStart(card)}
                    onDragEnd={handleDragEnd}
                    isDragging={draggedCard?.id === card.id}
                  />
                ))}

                {issues.length === 0 && (
                  <div className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                    暂无任务
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface KanbanCardProps {
  card: IssueCard
  projectId: string
  onDragStart: () => void
  onDragEnd: () => void
  isDragging: boolean
}

function KanbanCard({ card, projectId, onDragStart, onDragEnd, isDragging }: KanbanCardProps) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(
        'group relative block cursor-pointer rounded-md border border-l-4 bg-card p-3 shadow-sm transition-shadow hover:shadow-md',
        PRIORITY_COLORS[card.priority],
        isDragging && 'opacity-50'
      )}
    >
      {/* 快速操作按钮 - 悬停时显示 */}
      <div className="absolute right-1 top-1 opacity-0 transition-opacity group-hover:opacity-100">
        <QuickTimelogButton
          issueId={card.id}
          issueName={card.title}
          projectId={projectId}
        />
      </div>

      <div className="mb-2 text-xs text-muted-foreground">{card.code}</div>
      <div className="pr-6 font-medium leading-tight">{card.title}</div>
      {card.dueDate && (
        <div className="mt-2 text-xs text-muted-foreground">
          截止: {new Date(card.dueDate).toLocaleDateString('zh-CN')}
        </div>
      )}
      {/* 负责人和参与者 */}
      {(card.assigneeName || (card.participantCount && card.participantCount > 0)) && (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          {card.assigneeName && (
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-primary">
              {card.assigneeName}
            </span>
          )}
          {card.participantCount && card.participantCount > 0 && (
            <span className="text-muted-foreground">
              +{card.participantCount} 参与者
            </span>
          )}
        </div>
      )}
      {card.labels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {card.labels.map((label) => (
            <span
              key={label}
              className="rounded bg-muted px-1.5 py-0.5 text-xs"
            >
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
